import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Skeleton } from "../ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { ScrollArea } from "../ui/scroll-area";
import {
  BarChart3,
  PieChart,
  Target,
  CheckCircle2,
  Clock,
  XCircle,
  TrendingUp,
  Calendar,
  BookOpen,
  ChevronRight,
} from "lucide-react";
import { getSkillsCoverage, getWeeklySummary } from "../../lib/api";
import { MATIERES, getMatiereInfo } from "./constants";

const COLORS = {
  lecture: "#3B82F6",
  grammaire: "#8B5CF6",
  dictee: "#10B981",
  math: "#F97316",
  anglais: "#EF4444",
  sciences: "#06B6D4",
  histoire: "#A855F7",
  geographie: "#84CC16",
  arts: "#EC4899",
  eps: "#14B8A6",
  emc: "#6366F1",
  autre: "#64748B",
};

function StatCard({ icon: Icon, label, value, subvalue, color = "indigo" }) {
  return (
    <div className={`p-4 rounded-xl bg-${color}-50 border border-${color}-100`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-${color}-100`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-800">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
          {subvalue && <p className="text-xs text-slate-400 mt-0.5">{subvalue}</p>}
        </div>
      </div>
    </div>
  );
}

function MatiereBar({ matiere, count, total, maxCount }) {
  const info = getMatiereInfo(matiere);
  const percentage = total > 0 ? (count / total) * 100 : 0;
  const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0;
  
  return (
    <div className="flex items-center gap-3">
      <div className="w-24 flex items-center gap-2">
        {info?.icon && <info.icon className="w-4 h-4" style={{ color: info?.color }} />}
        <span className="text-sm font-medium truncate">{info?.label || matiere}</span>
      </div>
      <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ 
            width: `${barWidth}%`,
            backgroundColor: info?.color || COLORS[matiere] || COLORS.autre,
          }}
        />
      </div>
      <div className="w-16 text-right">
        <span className="text-sm font-bold text-slate-700">{count}</span>
        <span className="text-xs text-slate-400 ml-1">({percentage.toFixed(0)}%)</span>
      </div>
    </div>
  );
}

function SkillCoverageItem({ skill }) {
  const percentage = skill.count > 0 ? (skill.done / skill.count) * 100 : 0;
  
  return (
    <div className="p-3 bg-white rounded-lg border">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm font-medium text-slate-700 flex-1">{skill.label}</p>
        <Badge variant={percentage >= 100 ? "default" : "secondary"} className="text-xs">
          {skill.done}/{skill.count}
        </Badge>
      </div>
      <Progress value={percentage} className="h-2" />
      <div className="flex justify-between mt-1 text-xs text-slate-400">
        <span>{skill.planned} prevu(s)</span>
        <span>{skill.done} fait(s)</span>
      </div>
    </div>
  );
}

export function JournalStatsDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [coverage, setCoverage] = useState(null);
  const [weekSummary, setWeekSummary] = useState(null);
  const [period, setPeriod] = useState("month");
  const [selectedNiveau, setSelectedNiveau] = useState("all");

  useEffect(() => {
    if (open) {
      fetchStats();
    }
  }, [open, period, selectedNiveau]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      // Calculate date range based on period
      const today = new Date();
      let startDate, endDate;
      
      if (period === "week") {
        const monday = new Date(today);
        monday.setDate(today.getDate() - today.getDay() + 1);
        startDate = monday.toISOString().split("T")[0];
        endDate = today.toISOString().split("T")[0];
      } else if (period === "month") {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        startDate = firstDay.toISOString().split("T")[0];
        endDate = today.toISOString().split("T")[0];
      } else if (period === "trimester") {
        const firstDay = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        startDate = firstDay.toISOString().split("T")[0];
        endDate = today.toISOString().split("T")[0];
      } else {
        // Year
        const firstDay = new Date(today.getFullYear(), 0, 1);
        startDate = firstDay.toISOString().split("T")[0];
        endDate = today.toISOString().split("T")[0];
      }

      const params = { start_date: startDate, end_date: endDate };
      if (selectedNiveau !== "all") params.niveau = selectedNiveau;

      const [coverageRes, weekRes] = await Promise.all([
        getSkillsCoverage(params),
        getWeeklySummary(today.toISOString().split("T")[0]),
      ]);

      setCoverage(coverageRes.data);
      setWeekSummary(weekRes.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const totalSessions = coverage?.total_sessions || 0;
  const byMatiere = coverage?.by_matiere || {};
  const bySkill = coverage?.by_skill || [];
  const maxMatiereCount = Math.max(...Object.values(byMatiere), 1);

  const weekStats = weekSummary?.by_status || {};
  const completionRate = weekSummary?.completion_rate || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-500" />
            Statistiques et couverture du programme
          </DialogTitle>
        </DialogHeader>

        {/* Filters */}
        <div className="flex items-center gap-3 py-2 border-b">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Cette semaine</SelectItem>
              <SelectItem value="month">Ce mois</SelectItem>
              <SelectItem value="trimester">Ce trimestre</SelectItem>
              <SelectItem value="year">Cette annee</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedNiveau} onValueChange={setSelectedNiveau}>
            <SelectTrigger className="w-[100px] h-9">
              <SelectValue placeholder="Niveau" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="cp">CP</SelectItem>
              <SelectItem value="ce1">CE1</SelectItem>
              <SelectItem value="ce2">CE2</SelectItem>
              <SelectItem value="cm1">CM1</SelectItem>
              <SelectItem value="cm2">CM2</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <ScrollArea className="flex-1">
          {loading ? (
            <div className="space-y-4 p-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : (
            <div className="space-y-6 p-4">
              {/* Overview Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard
                  icon={Calendar}
                  label="Seances totales"
                  value={totalSessions}
                  color="indigo"
                />
                <StatCard
                  icon={CheckCircle2}
                  label="Faites cette semaine"
                  value={weekStats.fait || 0}
                  subvalue={`${completionRate}% acheve`}
                  color="green"
                />
                <StatCard
                  icon={Clock}
                  label="Prevues"
                  value={weekStats.prevu || 0}
                  color="blue"
                />
                <StatCard
                  icon={Target}
                  label="Competences liees"
                  value={bySkill.length}
                  color="purple"
                />
              </div>

              {/* Distribution by matiere */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <PieChart className="w-4 h-4" />
                    Repartition par matiere
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(byMatiere).length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">
                      Aucune séance enregistrée pour cette période
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(byMatiere)
                        .sort(([, a], [, b]) => b - a)
                        .map(([matiere, count]) => (
                          <MatiereBar
                            key={matiere}
                            matiere={matiere}
                            count={count}
                            total={totalSessions}
                            maxCount={maxMatiereCount}
                          />
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Skills coverage */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Couverture des competences
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {bySkill.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">
                      Aucune compétence liée aux séances pour cette période
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {bySkill.slice(0, 10).map((skill) => (
                        <SkillCoverageItem key={skill.skill_id} skill={skill} />
                      ))}
                      {bySkill.length > 10 && (
                        <p className="text-sm text-slate-400 text-center pt-2">
                          Et {bySkill.length - 10} autres competences...
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Weekly breakdown */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Resume de la semaine
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi"].map((day, i) => {
                      const dateKey = weekSummary?.by_day ? Object.keys(weekSummary.by_day)[i] : null;
                      const count = dateKey ? weekSummary.by_day[dateKey] : 0;
                      return (
                        <div
                          key={day}
                          className={`p-3 rounded-lg text-center ${
                            count > 0 ? "bg-indigo-50 border border-indigo-200" : "bg-slate-50"
                          }`}
                        >
                          <p className="text-xs font-medium text-slate-500">{day}</p>
                          <p className={`text-lg font-bold ${count > 0 ? "text-indigo-600" : "text-slate-300"}`}>
                            {count}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// Compact button to trigger stats dialog
export function JournalStatsButton({ onClick }) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      className="flex items-center gap-2"
    >
      <BarChart3 className="w-4 h-4" />
      <span className="hidden sm:inline">Statistiques</span>
    </Button>
  );
}
